<template>
	<div class="country-list">
		<CountryListItem v-if="!!availableWebLocale" :highlighted="true" :webLocale="availableWebLocale" />

		<hr />

		<SearchBar v-model="search" :placeholder="$t('WEBAPP_HYBRIDAPP_ONBOARDING_FILTER_COUNTRY')" />

		<CountryListItem
			v-for="item in webLocales"
			:key="item"
			:selected="item === languageStore.webLocale"
			:webLocale="item"
			@click="selectWebLocale(item)"
		/>
	</div>
</template>

<script setup lang="ts">
import type { WebLocale } from '@/enums/web-locale';
import type { SnowplowContext } from '@/helpers/tracking/providers';

import CountryListItem from '@/components/modals/country-list/CountryListItem.vue';
import SearchBar from '@/features/search/components/SearchBar.vue';

import { WEB_LOCALES } from '@/constants/web-locales.constant';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ref, computed } from 'vue';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

const props = withDefaults(defineProps<{ additionalContexts?: SnowplowContext[] }>(), {
	additionalContexts: () => [],
});

const emit = defineEmits<{ (e: 'selected-country', webLocale: WebLocale): void }>();

const languageStore = useLanguageStore();
const routingStore = useRoutingStore();

const { t } = useI18n();

const router = useRouter();

onMounted(languageStore.fetchLocales);

const search = ref('');

const localesWithPages = computed<Record<string, string>>(() =>
	Object.fromEntries((routingStore.urlMetadata?.allHrefLangTags ?? []).map(tl => [tl.href.slice(1, 3), tl.href]))
);

const availableWebLocale = computed(() =>
	languageStore.locales.some(locale => locale.webLocale === languageStore.webLocale) ? languageStore.webLocale : ''
);

const webLocales = computed(() => {
	const filteredLocales = languageStore.locales
		.filter(locale => {
			if (!(locale.webLocale in localesWithPages.value)) return false;
			if (!search.value) return true;

			return Object.values(locale.country_names)
				.map(name => name.toLowerCase())
				.concat(locale.country.toLowerCase())
				.some(name => name.includes(search.value.toLowerCase()));
		})
		.map(locale => locale.webLocale);

	return filteredLocales.sort((a, b) =>
		t(`WEBAPP_COUNTRY_${a.toUpperCase()}`).localeCompare(t(`WEBAPP_COUNTRY_${b.toUpperCase()}`))
	);
});

async function selectWebLocale(selectedLocale: WebLocale) {
	if (selectedLocale === languageStore.webLocale) return;

	const path = localesWithPages.value[selectedLocale];
	if (path == null) return;

	const localeString = WEB_LOCALES[selectedLocale] || '';
	const language = localeString.substring(0, 2);

	languageStore.setWebLocale(selectedLocale);
	await languageStore.setLanguageWhenUserDidNot(language);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'country_selector_toggled',
			property: selectedLocale,
		},
		props.additionalContexts
	);

	await router.push({ path });
	emit('selected-country', selectedLocale);
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.country-list {
	// NOTE Copied from old project. Some styles might be redundant.
	position: relative;

	&__search {
		width: calc(100% + 24px);
		margin: 0 -12px 10px;
		background-color: transparent;
		color: $jw-color-lightgray;
		padding-top: 5px !important;
		padding-inline-start: 10px !important;
	}
}
</style>
